// To be used only by managers/store modules

import {omit} from 'lodash';

import {mapAddressFromServerOrCookies} from '~/shared/utils/address';
import {retryApiRequest} from '~/shared/services/apiService/apiServiceUtils';

import {makeThunkAsyncActionCreator, makeActionCreator} from '../redux-toolbelt';
import {Address, IShoppingCart, ShoppingCartDish, User} from '../models';

export const updateAddress = makeActionCreator<'updateAddress', {address: Address; shouldUpdateAddressKey: boolean}>(
  'updateAddress',
);

export const clearOrderData = makeActionCreator<'clearOrderData', never>('clearOrderData');

export const setShoppingCartGuid = makeActionCreator<
  'setShoppingCartGuid',
  Partial<{shoppingCartGuid: string; shoppingCartGuidTimestamp: number}>
>('setShoppingCartGuid');

export const setBillingLines = makeActionCreator<'setBillingLines', IShoppingCart['billingLines']>('setBillingLines');
export const updateLastShoppingCartDishId = makeActionCreator<'updateLastShoppingCartDishId', number>(
  'updateLastShoppingCartDishId',
);
export const setDishes = makeActionCreator<'setDishes', ShoppingCartDish[]>('setDishes');

export const registerUser = makeThunkAsyncActionCreator<{
  signUpCredentials: SignUpCredentials;
  registerCompanyUser?: boolean;
}, User>(
  'registerUser',
  async ({signUpCredentials, registerCompanyUser}, {apiService}) => {
    const apiFunc = registerCompanyUser ? apiService.registerCompanyUser : apiService.registerUser;

    const {
      data: rawUser,
      errors,
      success: userRegisterSuccess,
    } = await apiFunc({autoGeneratePassword: true, ...signUpCredentials});

    if (!userRegisterSuccess) {
      return Promise.reject({register: {errors}});
    }

    return {...rawUser, addresses: []};
  },
);

export const login = makeThunkAsyncActionCreator<UserCredentials, User>(
  'login',
  async (userCredentials, {apiService}) => {
    const userLoginCredentials = omit(userCredentials, ['mode', 'returnUrl']);
    const isVerificationLogin = Boolean(
      'authenticationCode' in userLoginCredentials &&
        userLoginCredentials.authenticationCode &&
        userLoginCredentials.authenticationToken,
    );

    const apiServiceAction = isVerificationLogin ? 'getUserV2' : 'getUser';
    const loginAction = apiService[apiServiceAction];

    const {
      data: user,
      errors,
      success: userFetchSuccess,
    } = await retryApiRequest(() => loginAction(userLoginCredentials));

    if (userFetchSuccess === false) {
      const errorData = {requireCaptcha: user.requireCaptcha, errors};
      return Promise.reject({login: errorData});
    }

    const {data: addressesFromServer} = await apiService.getUserAddresses({});

    const addresses = addressesFromServer.map(mapAddressFromServerOrCookies) as Address[];
    return {...user, addresses};
  },
);

export const govLogin = makeThunkAsyncActionCreator<GovLogin, User>(
  'govLogin',
  async (userCredentials, {apiService}) => {
    const {data: user} = await apiService.govLogin(userCredentials);
    return user;
  },
);

export interface SignUpCredentials {
  firstName: string;
  lastName: string;
  cellPhone: string;
  wantPromotion: boolean;
  email: string;
  facebookUserId?: string;
  facebookUserAccessToken?: string;
  password?: string;
  activationToken?: string;
  activationCode?: string;
  autoGeneratePassword?: boolean;
  shoppingCartGuid?: string;
  websiteID?: string;
  domainID?: string;
  culture?: string;
  uiCulture?: string;
  agreeToTerms?: boolean;
  authenticationToken?: string;
  authenticationCode?: string;
  // token - used in B2B2C join page. defines a token provided by a specific company. on registration from /join page
  // a user should also provide a company token that comes in url params with the join link
  token?: string;
}

export interface LoginWithPassword {
  email: string;
  password: string;
}

export interface LoginWithProvider {
  facebookUserId?: string;
  facebookUserAccessToken?: string;
  googleUserId?: string;
  googleToken?: string;
}

interface LoginWithMobileCode {
  email: string;
  authenticationToken: string;
  authenticationCode: string;
}

export interface GovLogin {
  userLocalIdent: string; // gov personal id
  userLocalIdent01: string; // gov employee id / password
}

export type UserCredentials = Partial<LoginWithPassword | LoginWithMobileCode | LoginWithProvider>;
